import type { AxiosError } from 'axios';
import axios from 'axios';
import { errorTracker } from '@/common/error-tracking';
import { makeFullPath } from '@/utils/path-utils';

type NavigateFunction = (path: string) => void;

let navigateFunction: NavigateFunction | null = null;
let interceptorId: number | null = null;

/**
 * Register the navigate function from React Router.
 * This should be called from a component that has access to useNavigate().
 */
export function registerNavigate(navigate: NavigateFunction): void {
  navigateFunction = navigate;
}

/**
 * Unregister the navigate function.
 * This should be called during component cleanup to prevent stale references.
 */
export function unregisterNavigate(): void {
  navigateFunction = null;
}

/**
 * Helper function to handle error redirects with infinite loop prevention
 */
function handleErrorRedirect(targetPath: string): void {
  const currentPath = window.location.pathname;
  const fullTargetPath = makeFullPath(targetPath);

  // Prevent infinite redirect loop
  if (currentPath !== fullTargetPath) {
    // Use React Router navigation if available, otherwise fall back to window.location
    if (navigateFunction) {
      navigateFunction(fullTargetPath);
    } else {
      // Fallback for cases where React Router isn't initialized yet
      window.location.href = fullTargetPath;
    }
  }
}

type ErrorDetail =
  | string
  | { type?: string; error_code?: string; message?: string }
  | undefined;

// Top-level in the 403 body, not nested in `detail` like the 401 envelope.
const ACCESS_DENIED_BY_POLICY_ERROR_CODE = 'AUTH_ACCESS_DENIED';

function isStaleSession(detail: ErrorDetail): boolean {
  if (typeof detail === 'string') {
    // FastAPI's HTTPException(detail=...) emits a plain string for the three
    // session types auth manages:
    //   - MFA session: "MFA session expired." / "Invalid or missing mfa session."
    //   - Pending auth: "Pending auth session expired." / "Invalid pending auth
    //     session." / "Missing pending auth session."
    //   - IdP session:  "IdP session expired." / "Invalid IdP session."
    //   - MFA-start with neither pending-auth cookie nor body auth_id:
    //     "Authentication session not found. Please reload the page and try again."
    // The cookie-missing MFA path also emits the structured {error_code} envelope.
    return /mfa session|pending auth session|idp session|authentication session not found/i.test(
      detail
    );
  }

  return detail?.error_code === 'MFA_SESSION_REQUIRED';
}

/**
 * Endpoints whose callers already handle a stale-session 401 themselves, so
 * the interceptor MUST NOT hard-reload to `/login` on a 401 from these:
 *
 * - `/login` bootstrap requests (fired inside `ValidatingPage`): reloading
 *   would infinite-loop because the cookie is still expired/missing on the
 *   very next request after the reload. Their callers tolerate the failure
 *   via try/catch → `initiateSamlOrFallback`.
 * - Consent-page requests: `ConsentPage` renders an inline session-expired
 *   card; a reload to `/login` would replace it with a broken login flow
 *   (there is no auth_id to resume).
 * - The `/mfa/status` poller: a reload re-bootstraps the flow and fires a new
 *   push, so a 2s poll against a dead session becomes a push-spam loop
 *   (SPRK-6475). `useMfaPolling` stops itself and shows the expired card.
 *
 * !!! KEEP IN SYNC !!!
 * Whenever a new request is added to the `/login` bootstrap path
 * (`pages/auth/validating-page.tsx` or anything else mounted by `LoginPage`),
 * append its URL suffix here. If you forget, stale-session reloads will
 * loop on that endpoint.
 */
const STALE_SESSION_RELOAD_EXEMPT_URL_SUFFIXES = [
  '/validate-auth-id', // pendingAuthService.validateAuthId
  '/mfa/orchestrator/start-from-cookie', // mfaOrchestrationService.startMfaFlowFromCookie
  '/oauth/consent-info', // oauthService.getConsentInfo
  '/oauth/consent', // oauthService.postConsent
  '/oauth/consent/switch-user', // oauthService.switchUser
  '/mfa/status', // useMfaPolling.pollStatus
];

function isStaleSessionReloadExempt(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  return STALE_SESSION_RELOAD_EXEMPT_URL_SUFFIXES.some(suffix =>
    url.endsWith(suffix)
  );
}

/**
 * Sets up global axios interceptor to handle authentication errors.
 *
 * This interceptor will catch:
 * - 403 with error_code 'AUTH_ACCESS_DENIED' (the matched access policy's
 *   effect is Block) → redirect to the access-denied-by-policy page
 * - any other 403 Forbidden response → redirect to unauthorized page
 * - 401 with type 'insufficient_enrollment' → redirect to auth error page
 * - 401 matching an expired/missing server-side session (MFA, pending-auth,
 *   or IdP) → hard-reload to the login page so the broken flow is restarted
 *   instead of silently looping. A full reload (rather than React Router
 *   navigation) is required because the user may already be on
 *   `/login?auth_id=...` — a pathname-only guard would be a no-op and the
 *   broken flow would continue. Reloads are skipped for endpoints whose
 *   callers handle the stale session themselves (see
 *   `STALE_SESSION_RELOAD_EXEMPT_URL_SUFFIXES`).
 *
 * This function is idempotent - calling it multiple times will not register
 * duplicate interceptors. This prevents issues with hot module replacement (HMR)
 * in development environments.
 */
export function setupAxiosInterceptors(): void {
  // If interceptor is already registered, don't register it again
  if (interceptorId !== null) {
    return;
  }

  interceptorId = axios.interceptors.response.use(
    response => {
      // Pass through successful responses unchanged
      return response;
    },
    (error: AxiosError) => {
      // Check if the error is a 403 Forbidden response
      if (error.response?.status === 403) {
        const errorCode = (error.response.data as { error_code?: string })
          ?.error_code;
        handleErrorRedirect(
          errorCode === ACCESS_DENIED_BY_POLICY_ERROR_CODE
            ? '/access-denied-by-policy'
            : '/unauthorized'
        );
      }

      if (error.response?.status === 401) {
        const detail = (error.response?.data as { detail?: ErrorDetail })
          ?.detail;

        if (
          typeof detail === 'object' &&
          detail?.type === 'insufficient_enrollment'
        ) {
          handleErrorRedirect('/auth-error');
        } else if (isStaleSession(detail)) {
          // Stale server-side session (MFA / pending auth / IdP). Force a full
          // page reload to the bare /login route: this drops stale search
          // params (authId, escalation) so the flow restarts cleanly — the
          // validating page either resumes on the factor (if a sibling cookie
          // is still alive) or falls through to initiateSamlOrFallback().
          //
          // Skip the reload for exempt requests (see
          // STALE_SESSION_RELOAD_EXEMPT_URL_SUFFIXES) — those callers
          // already handle the failure gracefully themselves.
          if (!isStaleSessionReloadExempt(error.config?.url)) {
            errorTracker.captureError('Stale session, reloading /login', {
              level: 'warning',
              tags: {
                type: 'stale_session',
                detail_shape:
                  typeof detail === 'string' ? 'string' : 'envelope',
              },
              extra: { detail, url: error.config?.url },
            });
            window.location.href = makeFullPath('/login');
          }
        }
        // Otherwise let it fall through for component-level error handling
      }

      // Always reject the promise so the original caller can still handle the error if needed
      return Promise.reject(error);
    }
  );
}

/**
 * Removes the axios interceptor if it was previously registered.
 * Useful for cleanup in tests or when the app is being torn down.
 */
export function cleanupAxiosInterceptors(): void {
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
    interceptorId = null;
  }
}
